import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "../components/Layout";
import KPICard from "../components/dashboard/KPICard";
import AlertsList from "../components/dashboard/AlertsList";
import AOVTrendChart from "../components/dashboard/AOVTrendChart";
import MBATopPairs from "../components/dashboard/MBATopPairs";
import PurchaseRecommendationsTable from "../components/dashboard/PurchaseRecommendationsTable";
import { useAuth } from "../contexts/AuthContext";
import {
  ApiError,
  endpoints,
  type AOVTrend,
  type Alert,
  type BundleProposal,
  type KPI,
  type MBAPair,
  type PurchaseRecommendation,
  type UpsellPotential,
} from "../services/api";

const PERIOD_DAYS = 30;

function formatPLN(value: string | number): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("pl-PL", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function formatNumber(value: number, fractionDigits = 0): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("pl-PL", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { currentStoreId } = useAuth();
  const [kpi, setKPI] = useState<KPI | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [pairs, setPairs] = useState<MBAPair[]>([]);
  const [bundles, setBundles] = useState<BundleProposal[]>([]);
  const [trend, setTrend] = useState<AOVTrend | null>(null);
  const [recs, setRecs] = useState<PurchaseRecommendation[]>([]);
  const [upsell, setUpsell] = useState<UpsellPotential | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentStoreId === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      endpoints.kpi(currentStoreId, PERIOD_DAYS),
      endpoints.alerts(currentStoreId, true, 10),
      endpoints.mba(currentStoreId, PERIOD_DAYS, 10),
      endpoints.bundles(currentStoreId, PERIOD_DAYS, 5),
      endpoints.aovTrend(currentStoreId, PERIOD_DAYS),
      endpoints.purchaseRecommendations(currentStoreId, 15),
      endpoints.upsellPotential(currentStoreId, PERIOD_DAYS),
    ])
      .then(([k, a, p, b, tr, r, up]) => {
        if (cancelled) return;
        setKPI(k);
        setAlerts(a);
        setPairs(p);
        setBundles(b);
        setTrend(tr);
        setRecs(r);
        setUpsell(up);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : "Błąd ładowania danych");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStoreId]);

  const isAggregate = kpi?.source === "aggregate";

  return (
    <Layout>
      <header className="page-header">
        <h1>{t("dashboard.title")}</h1>
        <span className="page-header__hint">Okres: ostatnie {PERIOD_DAYS} dni</span>
      </header>

      {currentStoreId === null && (
        <section className="card empty-state">
          <p>Wybierz sklep, aby zobaczyć dane.</p>
        </section>
      )}

      {currentStoreId !== null && loading && (
        <section className="card">
          <p>Ładowanie…</p>
        </section>
      )}

      {error && (
        <section className="card empty-state">
          <p style={{ color: "var(--color-danger)" }}>{error}</p>
        </section>
      )}

      {!loading && !error && kpi !== null && kpi.source === "empty" && (
        <section className="card empty-state">
          <h2>Brak danych</h2>
          <p>Wgraj plik z Symplexa (raport „Najczęściej w koszyku"), żeby zobaczyć swoje wskaźniki.</p>
          <Link to="/import" className="btn btn--primary">
            Wgraj raport →
          </Link>
        </section>
      )}

      {!loading && !error && kpi !== null && kpi.source !== "empty" && (
        <>
          {isAggregate && (
            <section className="card info-banner">
              <strong>Tryb: agregat per produkt.</strong> KPI policzone z raportu „Najczęściej
              w koszyku" w wariancie Excel 2003 (per-produkt). Marża, alerty czasowe i pary MBA
              wymagają raportu transakcyjnego z Nr dokumentu.
            </section>
          )}

          <AlertsList alerts={alerts} onChange={setAlerts} />

          <section className="kpi-grid">
            <KPICard
              label={t("dashboard.kpi.aov")}
              value={formatPLN(kpi.avg_basket)}
              unit="zł"
            />
            <KPICard
              label={t("dashboard.kpi.itemsPerBasket")}
              value={formatNumber(kpi.items_per_basket, 1)}
              hint={isAggregate ? "przybliżenie z agregatu" : undefined}
            />
            <KPICard
              label={t("dashboard.kpi.transactions")}
              value={formatNumber(kpi.transactions)}
              hint={isAggregate ? "max(ilość dokumentów)" : undefined}
            />
            <KPICard
              label={t("dashboard.kpi.margin")}
              value={isAggregate ? "—" : formatPct(kpi.gross_margin_pct)}
              unit={isAggregate ? "" : "%"}
              hint={isAggregate ? "brak w raporcie" : undefined}
            />
            {upsell && (
              <KPICard
                label="Potencjał wzrostu z bundli"
                value={formatPLN(upsell.monthly_uplift_pln)}
                unit="zł/m-c"
                hint={`${upsell.bundles_count} aktywnych zestawów`}
              />
            )}
          </section>

          {trend && <AOVTrendChart trend={trend} />}

          <PurchaseRecommendationsTable rows={recs} />

          <MBATopPairs pairs={pairs} />

          {bundles.length > 0 && (
            <section className="card">
              <h2 className="card__title">💼 Propozycje zestawów</h2>
              <ul className="bundles">
                {bundles.map((b, i) => (
                  <li key={i} className="bundle">
                    <div className="bundle__products">
                      {b.products.map((pr) => pr.name).join(" + ")}
                    </div>
                    <div className="bundle__metrics">
                      <span>Cena: {formatPLN(b.bundle_total_price)} zł</span>
                      <span>Wzrost koszyka: +{(b.expected_aov_uplift * 100).toFixed(1)}%</span>
                      <span>Marża zest.: {formatPLN(b.bundle_margin_pct)}%</span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </Layout>
  );
}
